import React, { useMemo } from 'react';
import _ from 'lodash';

import { gql } from '@nerdwallet/apollo';
import { Accent, Link, Stack, Text } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression as BedrockImpression } from '@nerdwallet/analytics/react/impression';
import {
  setTrk,
  TrkTag,
  TrkElement,
  TrkPageType,
} from '@nerdwallet/nw-client-lib/trk-helper';

import WP_POST_LIMITS from '~/lib/constants/wp-post-limits';
import { getArticleTypeConfig } from '~/lib/article-utils';
import BlockGroup from '../block-group/block-group';
import { checkComponentShouldTriggerLegacyImpressions } from '~/lib/legacy';

import { NextStepsFragment } from '~/generated-gql/generated-types';

interface Props {
  article: NextStepsFragment;
}

const CATEGORY_LINK_TEXT = 'Explore';

const NextSteps: React.GraphqlFC<Props> = ({ article }) => {
  const { nextSteps: blockGroup, primaryCategory } = article;
  const { name, link } = primaryCategory || {};

  const shouldTriggerLegacyImpressions = useMemo(
    () => checkComponentShouldTriggerLegacyImpressions('NextSteps'),
    []
  );

  const href = setTrk({
    url: link,
    params: {
      [TrkTag.element]: TrkElement.hyperlink,
      [TrkTag.location]: 'NextSteps',
      [TrkTag.pageType]: TrkPageType.article,
      [TrkTag.copy]: `${CATEGORY_LINK_TEXT} ${name}`,
    },
  });

  const children = (
    <BedrockImpression
      eventType="content_element_impression"
      entityName="next_steps"
      payload={{
        content_source: 'wp',
        content_id: parseInt(blockGroup.id, 10),
        content_id_type: 'post',
      }}
    >
      <Stack dividers={2}>
        <Stack spacing="4">
          <Accent size="small">Dive even deeper in {name}</Accent>
          <BlockGroup
            blockGroup={blockGroup}
            wpPostListLimit={WP_POST_LIMITS.NEXT_STEPS}
            excludeArticleId={parseInt(article.id, 10)}
          />
        </Stack>
        <Link href={href}>
          <Text>{CATEGORY_LINK_TEXT}</Text> <Accent>{name}</Accent>
        </Link>
      </Stack>
    </BedrockImpression>
  );

  return shouldTriggerLegacyImpressions ? (
    <ElementImpression
      id="article-client-next-steps"
      elementName="NextSteps"
      sectionCategory="belowContent"
    >
      {children}
    </ElementImpression>
  ) : (
    children
  );
};

NextSteps.fragment = gql`
  fragment NextSteps on Article {
    id
    articleType
    nextSteps {
      ...BlockGroup
    }
    primaryCategory {
      name
      link
    }
  }
  ${BlockGroup.fragment}
`;

NextSteps.shouldShow = (article) => {
  const articleTypeConfig = getArticleTypeConfig(article?.articleType);
  const blockGroup = article?.nextSteps;
  return _.get(articleTypeConfig, 'hasNextSteps', false) && !!blockGroup;
};

export default NextSteps;
